import { useToast } from '@/hooks/use-toast';
import type { BloodTest, BloodTestWorkspaceProps, BloodTestWorkspaceState } from '@careflux/mercury';
import { useIsMutating } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { resolveTaskTestSelection } from './bloodTestCatalog';
import {
  buildRelations,
  lockedIds,
  recommendedFor,
  resolveNames,
  toggleTest,
  withIncluded,
} from './bloodTestRelations';
import type { LabOrderItemResponse, LabOrderResponse, UseBloodTestOrderingOptions } from './bloodTests.types';
import { labOrderErrorMessage } from './labOrderMessages';
import { LAB_ORDER_MUTATION_KEY, useCreateLabOrder } from './useCreateLabOrder';
import { useLabTests } from './useLabTests';

const codesWithStatus = (result: LabOrderResponse, status: LabOrderItemResponse['status']): Set<string> =>
  new Set(result.items.filter((item) => item.status === status).flatMap((item) => item.test_codes));

const testNames = (tests: BloodTest[]) => tests.map((test) => test.name).join(', ');

const UNCONFIRMED_TITLE = 'Verið er að staðfesta pöntun';
const DO_NOT_RESEND = 'Ekki senda aftur — athugaðu stöðuna eftir augnablik.';

/**
 * Scribe wiring for Mercury's BloodTestWorkspace: maps the lab catalog, owns the
 * workspace state (preselected from the todo text), and places the order.
 */
export const useBloodTestOrdering = ({ todoId, details, extraData, onOrdered }: UseBloodTestOrderingOptions) => {
  const { toast } = useToast();
  const catalog = useLabTests();
  const mutation = useCreateLabOrder();
  const inFlight = useRef(false);
  // A reopened sheet (remounted on a new todoId key) gets a fresh mutation
  // instance, so mutation.isPending alone misses an earlier mount's submit
  // that's still in flight — useIsMutating sees it across mounts by key.
  const pendingOrders = useIsMutating({ mutationKey: [...LAB_ORDER_MUTATION_KEY] });
  const orderInFlight = mutation.isPending || pendingOrders > 0;
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [draft, setDraft] = useState<BloodTestWorkspaceState | null>(null);

  const relations = useMemo(() => buildRelations(catalog.data ?? []), [catalog.data]);
  const selection = useMemo(
    () => resolveTaskTestSelection(catalog.data ?? [], details, extraData),
    [catalog.data, details, extraData]
  );
  const initialState = useMemo<BloodTestWorkspaceState>(
    () => ({
      selectedTests: withIncluded(selection.testCodes, relations),
      notes: '',
      status: 'draft',
      draftResponse: '',
    }),
    [selection, relations]
  );
  const state = draft ?? initialState;
  const setSelected = (selectedTests: BloodTest[]) => setDraft({ ...state, selectedTests });

  const recommendedTests = recommendedFor(state.selectedTests, relations);
  const resolvedIncludedNames = useMemo(() => resolveNames(relations.included, relations), [relations]);
  const resolvedRecommendedNames = useMemo(() => resolveNames(relations.recommended, relations), [relations]);

  // Unknown items may still become orders: no resubmit from this sheet.
  const markUnconfirmed = (description: string) => {
    setAwaitingConfirmation(true);
    toast({ title: UNCONFIRMED_TITLE, description });
  };

  const handlePartial = (result: LabOrderResponse) => {
    const failed = codesWithStatus(result, 'failed');
    const unknown = codesWithStatus(result, 'unknown');
    const failedTests = state.selectedTests.filter((test) => failed.has(test.id));
    const unknownTests = state.selectedTests.filter((test) => unknown.has(test.id));
    const unconfirmed = unknownTests.length ? `Staða óstaðfest: ${testNames(unknownTests)}. ${DO_NOT_RESEND} ` : '';
    if (failedTests.length === 0) {
      markUnconfirmed(`${unconfirmed}Aðrar rannsóknir voru pantaðar.`);
      return;
    }
    toast({
      title: 'Sumar rannsóknir voru ekki pantaðar',
      description: `Ekki tókst að panta: ${testNames(failedTests)}. ${unconfirmed}Aðrar rannsóknir voru pantaðar.`,
      variant: 'destructive',
    });
    // Keep only the failed tests so a retry re-orders neither created nor unconfirmed ones.
    setSelected(failedTests);
  };

  const submitOrder = async (patientIdentifier: string) => {
    if (inFlight.current || orderInFlight || awaitingConfirmation) return;
    if (state.selectedTests.length === 0) {
      toast({ title: 'Engin rannsókn valin', description: 'Veldu að minnsta kosti eina rannsókn.' });
      return;
    }
    inFlight.current = true;
    const comment = state.notes.trim();
    try {
      const result = await mutation.mutateAsync({
        patient_identifier: patientIdentifier,
        test_codes: state.selectedTests.map((test) => test.id),
        ...(comment ? { comment } : {}),
        context_id: String(todoId),
      });
      if (result.status === 'partial_success') {
        handlePartial(result);
        return;
      }
      if (result.status === 'unknown') {
        markUnconfirmed(`Staða pöntunarinnar er óstaðfest. ${DO_NOT_RESEND}`);
        return;
      }
      toast({ title: 'Blóðprufubeiðni send', description: 'Rannsóknirnar voru pantaðar.' });
      onOrdered();
    } catch (error) {
      toast({ title: 'Villa', description: labOrderErrorMessage(error), variant: 'destructive' });
    } finally {
      inFlight.current = false;
    }
  };

  const workspaceProps: Omit<BloodTestWorkspaceProps, 'onConfirm'> = {
    state,
    tests: relations.tests,
    // Mercury marks the step "confirmed" after onConfirm resolves, whatever the
    // outcome; this hook owns the outcome, so that transition is ignored.
    onStateChange: (next) => {
      if (next.status !== 'confirmed') setDraft(next);
    },
    isConfirming: orderInFlight || awaitingConfirmation,
    lockedTestIds: lockedIds(state.selectedTests, relations),
    recommendedTests,
    resolvedIncludedNames,
    resolvedRecommendedNames,
    onToggleTest: (test) => setSelected(toggleTest(state.selectedTests, test, relations)),
    onAddAllRecommended: () => setSelected([...state.selectedTests, ...recommendedTests]),
  };

  return { catalog, workspaceProps, submitOrder, unresolvedTerms: selection.unresolvedTerms };
};
